import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { getListPrice } from "~/domains/product/utils/productDescriptorUtils";
import { Price, Price as PriceV3, type PriceProps } from "~/domains/pricing/components/Price/v3/Price";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import type { NetPriceData, PriceData } from "~/domains/pricing/types/PriceData";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { Box, CircularProgress, Stack, styled } from "~/common/mui";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import type { AddToCartFormDraft, OptionalPartViewModel } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { KitPrices } from "~/features/productDetails/components/ProductSummary/components/KitPrices";
import { calculateSalesCampaign4PDP, useSelectPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import { canSeeListPrice } from "~/utils/features";
import { multiply, volatileSum, zero } from "~/utils/PriceUtils";
import { isKITProduct } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Parameter } from "~/common/components/ParameterV2";
import { GPSDParam } from "~/domains/product/components/ProductParamsV3";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";

interface PdpPriceBlockProps {
  descriptor: ProductDescriptor;
  packCode: string;
  priceReference: string;
  quantity: number;
}

export const PdpPriceBlock = (props: PdpPriceBlockProps) => {
  const formProps = useFormContext<AddToCartFormDraft>();
  const { main, optionalParts: derivedOptionalParts } = useAddToCartDerived();
  const optionalPartDrafts = formProps.watch("optionalParts");
  const salePriceEnabled = formProps.watch("salePriceEnabled");

  const selectedParts = Object.entries(derivedOptionalParts.byId)
    .filter(([id]) => optionalPartDrafts[id]?.selected)
    .map(([, part]) => part);

  return (
    <PriceBlock {...props} selectedParts={selectedParts} netPrice={main.netPrice} salePriceEnabled={salePriceEnabled} />
  );
};

interface PriceBlockProps {
  loading?: boolean;
  descriptor: ProductDescriptor;
  packCode: string;
  priceReference: string;
  quantity: number;
  selectedParts: OptionalPartViewModel[];
  netPrice: NetPriceData | null;
  salePriceEnabled?: boolean;
}

export const PriceBlock = ({
  descriptor,
  packCode,
  priceReference,
  quantity,
  selectedParts,
  netPrice,
  salePriceEnabled,
  loading,
}: PriceBlockProps) => {
  if (!userHasPermission(Permission.PRICE)) {
    return <GPSDParam product={descriptor.base} />;
  }
  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100px" }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  const product = descriptor.base;
  const isHousingKit = isKITProduct(product);
  const hasSurcharge = (netPrice?.surcharge ?? 0) > 0;

  const countTotal = (price: PriceData | null, priceUnit: number, qty: number) => {
    return !price ? null : multiply(price, qty / priceUnit);
  };

  if (isHousingKit) {
    const price = netPrice;
    const priceUnit = netPrice?.unit ?? 1;

    const optionalPartsPrice = volatileSum(
      ...selectedParts.map(
        (part) => countTotal(part.derived.netPrice, part.derived.netPrice?.unit ?? 1, part.draft.quantity) ?? zero(),
      ),
    );
    const optionalPartsHaveSurcharge =
      selectedParts.length > 0 && selectedParts.some((part) => (part.derived.netPrice?.surcharge ?? 0) > 0);
    const totalPrice = countTotal(price, priceUnit, quantity);

    return (
      <Stack gap={2}>
        <KitPrices
          totalPrice={totalPrice ?? zero()}
          optionalPartsPrice={optionalPartsPrice}
          product={product}
          includesSurcharge={hasSurcharge}
          optionalPartsIncludesSurcharge={optionalPartsHaveSurcharge}
        />
        <GPSDParam product={product} />
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <SalePriceSection
        netPrice={netPrice}
        descriptor={descriptor}
        packCode={packCode}
        quantity={quantity}
        priceReference={priceReference}
        salePriceEnabled={salePriceEnabled}
      />
      <PricingMetaRow>
        <ListPriceSection
          descriptor={descriptor}
          packCode={packCode}
          priceReference={priceReference}
          salePriceEnabled={salePriceEnabled}
          quantity={quantity}
        />
        <GPSDParam product={product} />
      </PricingMetaRow>
    </Stack>
  );
};

interface ListPriceSectionProps {
  descriptor: ProductDescriptor;
  packCode: string;
  priceReference: string;
  salePriceEnabled?: boolean;
  quantity: number;
}

const ListPriceSection = ({
  descriptor,
  packCode,
  priceReference,
  salePriceEnabled,
  quantity,
}: ListPriceSectionProps) => {
  const isOnSale = useSalesHelper(descriptor).isPackageOnSale(packCode);
  const saleActive = isOnSale && salePriceEnabled;
  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor: descriptor }), [descriptor]);
  const saleForPack = data?.salePerPack?.[packCode];
  const { netPrice } = useSelectPricingDetails({
    packCode,
    quantity,
    priceReference,
    productCode: descriptor.base.code,
    salesData: saleForPack,
    saleActivated: !!saleActive,
  });
  const hasSurcharge = (netPrice?.surcharge ?? 0) > 0;
  const listPrice = getListPrice({ product: descriptor, packCode });
  const showListPrice =
    !saleActive &&
    !!listPrice &&
    canSeeListPrice({
      product: descriptor,
      isSpecialPrice: netPrice?.isSpecial ?? false,
      hasSurcharge,
    });

  if (!showListPrice) {
    return null;
  }

  return (
    <Parameter
      label={<PriceElements.ListPriceLabel unit={netPrice?.unit ?? 1} variant="inherit" />}
      value={<PriceElements.Price price={listPrice} variant="inherit" />}
    />
  );
};

interface SalePriceSectionProps {
  descriptor: ProductDescriptor;
  packCode: string;
  priceReference: string;
  quantity: number;
  netPrice: NetPriceData | null;
  salePriceEnabled?: boolean;
}

const useSalePriceData = ({
  descriptor,
  packCode,
  priceReference,
  quantity,
  netPrice,
  salePriceEnabled,
}: SalePriceSectionProps) => {
  const product = descriptor.base;
  const isOnSale = useSalesHelper(descriptor).isPackageOnSale(packCode);
  const saleActive = isOnSale && salePriceEnabled;
  const tariffSurcharge = isTariffSurchargeEnabled() && (netPrice?.tariffSurcharge ?? false);
  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor: descriptor }), [descriptor]);
  const saleForPack = data?.salePerPack?.[packCode];
  const pricingDetails = useSelectPricingDetails({
    packCode,
    quantity,
    priceReference,
    productCode: product.code,
    salesData: saleForPack,
    saleActivated: !!saleActive,
  });

  return {
    pricingDetails,
    saleActive,
    saleForPack,
    tariffSurcharge,
  };
};

export const SalePriceSection = ({
  descriptor,
  packCode,
  priceReference,
  quantity,
  netPrice,
  salePriceEnabled,
}: SalePriceSectionProps) => {
  const {
    pricingDetails: { discountFormatted, oldPrice, price },
    saleActive,
    saleForPack,
    tariffSurcharge,
  } = useSalePriceData({ descriptor, packCode, priceReference, quantity, netPrice, salePriceEnabled });
  const hasSurcharge = (netPrice?.surcharge ?? 0) > 0;
  const isSoldOut = saleForPack?.soldOut ?? false;
  const { t } = useTranslation();
  const somePriceValid = (price?.value ?? 0) > 0 || (oldPrice?.value ?? 0) > 0;

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          <CurrentPrice
            price={price}
            zeroText={t("product.priceOnRequest")}
            tariffSurcharge={tariffSurcharge}
            soldOut={isSoldOut}
            surchargeEnabled={hasSurcharge}
          />
          {saleActive && (
            <SaleDiscountIndicator.V2
              discountFormatted={discountFormatted ?? null}
              soldOut={false}
              variant="titleLarge"
            />
          )}
        </Stack>
        {oldPrice && <OldPrice price={oldPrice} soldOut={isSoldOut} />}
        {somePriceValid && (
          <>
            {<PriceV3.NetPriceUnit price={netPrice} />}
            {tariffSurcharge && <p className="label-small mb-1">{t("price.tariff.surcharge.applicable")}</p>}
            {!saleActive && <PriceV3.ExtraLabels price={netPrice} surchargeEnabled={hasSurcharge} />}
          </>
        )}
      </Stack>
      {saleActive && saleForPack && <SaleQuantityIndicator.V2 quantity={saleForPack.salesStock} variant="labelLarge" />}
    </Stack>
  );
};

const PricingMetaRow = styled(Stack)(({ theme }) => ({
  alignItems: "flex-start",
  gap: theme.spacing(2),
  flexDirection: "column",
  [theme.breakpoints.up("tablet")]: {
    flexDirection: "row",
    "& > *": {
      flex: 1,
    },
  },
}));

const CurrentPrice = styled(Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.HeadlineMedium),
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));

PriceBlock.CurrentPrice = CurrentPrice;
PriceBlock.SalePriceSection = SalePriceSection;
PriceBlock.PricingMetaRow = PricingMetaRow;
PriceBlock.ListPriceSection = ListPriceSection;
